import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Moon, Sun, Menu, X, ShoppingCart, Search } from 'lucide-react';
import { useTheme } from '@/lib/theme';
import api from '@/lib/api';
import SafeImg from '@/components/SafeImg';

const Navigation = ({ user, setUser, compact = false }) => {
  const { theme, toggleTheme } = useTheme();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sellerStatus, setSellerStatus] = useState(user?.seller_status || 'none');
  const [cartCount, setCartCount] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    setSellerStatus(user?.seller_status || 'none');
  }, [user?.seller_status]);

  // Live cart-count badge. Refreshes on mount, on route change, and whenever
  // any Cart mutation dispatches `livemoa:cart-updated`.
  useEffect(() => {
    if (!user) { setCartCount(0); return; }
    let cancelled = false;
    const refresh = async () => {
      try {
        const r = await api.get('/cart');
        if (!cancelled) setCartCount((r.data?.items || []).length);
      } catch { /* silent — 401 handled elsewhere */ }
    };
    refresh();
    const onUpdated = () => refresh();
    window.addEventListener('livemoa:cart-updated', onUpdated);
    return () => { cancelled = true; window.removeEventListener('livemoa:cart-updated', onUpdated); };
  }, [user?.user_id]);

  const handleLogout = async () => {
    try {
      await api.post('/auth/logout');
      setUser(null);
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  // Seller-related menu label based on seller_status
  const sellerLink = {
    none:      { label: '판매자 신청',   to: '/seller/apply' },
    pending:   { label: '심사 중',       to: '/seller/apply' },
    approved:  { label: '판매자 대시보드', to: '/seller/dashboard' },
    rejected:  { label: '재신청',        to: '/seller/apply' },
    suspended: { label: '이용 정지',     to: '/seller/apply' },
  }[sellerStatus] || { label: '판매자 신청', to: '/seller/apply' };

  // Menu group grouped to the right, near the theme toggle.
  const renderCoreLinks = (isMobile = false) => {
    const base = 'text-sm font-medium hover:text-[#FF3B30] transition-colors';
    const cls = isMobile ? `block ${base}` : base;
    const onClick = isMobile ? () => setMobileMenuOpen(false) : undefined;

    return (
      <>
        <Link to="/schedule" className={cls} onClick={onClick} data-testid={isMobile ? 'nav-schedule-mobile' : 'nav-schedule'}>
          스케줄
        </Link>

        {user ? (
          <>
            <Link to="/dashboard" className={cls} onClick={onClick} data-testid={isMobile ? 'nav-dashboard-mobile' : 'nav-dashboard'}>
              마이페이지
            </Link>
            <Link to="/cart" onClick={onClick}
              className={`${cls} inline-flex items-center gap-1`}
              data-testid={isMobile ? 'nav-cart-mobile' : 'nav-cart'}>
              <ShoppingCart size={14} />
              <span>장바구니</span>
              {cartCount > 0 && (
                <span
                  className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#FF3B30] text-white text-[10px] font-black leading-none"
                  data-testid={isMobile ? 'nav-cart-badge-mobile' : 'nav-cart-badge'}
                >{cartCount}</span>
              )}
            </Link>
            <Link to={sellerLink.to} className={cls} onClick={onClick} data-testid={isMobile ? 'nav-seller-mobile' : 'nav-seller'}>
              {sellerLink.label}
            </Link>
          </>
        ) : (
          <Link to="/search" className={cls} onClick={onClick} data-testid={isMobile ? 'nav-find-sellers-mobile' : 'nav-find-sellers'}>
            판매자 찾기
          </Link>
        )}
      </>
    );
  };

  return (
    <nav className="sticky top-0 z-50 backdrop-blur-xl bg-white/70 dark:bg-black/60 border-b border-black/5 dark:border-white/10"
         data-nav-compact={compact ? 'true' : 'false'}
         data-testid={compact ? 'nav-compact' : 'nav-default'}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`flex justify-between items-center ${compact ? 'h-11' : 'h-16'}`}>
          <Link to="/" className="flex items-center" data-testid="nav-logo">
            <div className={`${compact ? 'text-lg' : 'text-2xl'} font-black tracking-tight`} style={{ fontFamily: 'Manrope, sans-serif' }}>
              <span className="text-[#FF3B30]">LIVE</span>
              <span className="text-black dark:text-white">MOA</span>
            </div>
          </Link>

          {/* Desktop right-side menu group */}
          <div className={`hidden md:flex items-center ${compact ? 'gap-4' : 'gap-6'}`} data-testid="nav-desktop-group">
            {renderCoreLinks(false)}

            <div className="h-5 w-px bg-black/10 dark:bg-white/10" aria-hidden="true" />

            {/* Phase 6.0 · 검색 돋보기 (기존 큰 검색바 대체) */}
            <button
              onClick={() => setSearchOpen((v) => !v)}
              className="p-2 rounded-md hover:bg-black/5 dark:hover:bg-white/10 transition-colors"
              aria-label="검색"
              data-testid="nav-search-toggle"
            >
              <Search size={18} />
            </button>

            <button
              onClick={toggleTheme}
              className="p-2 rounded-md hover:bg-black/5 dark:hover:bg-white/10 transition-colors"
              data-testid="theme-toggle"
              aria-label="Toggle theme"
            >
              {theme === 'light' ? <Moon size={18} /> : <Sun size={18} />}
            </button>

            {user ? (
              <div className="flex items-center gap-3">
                <Link to="/dashboard" data-testid="profile-icon-link" aria-label="My Page">
                  <SafeImg
                    src={user.picture}
                    kind="avatar"
                    alt={user.name}
                    className="w-8 h-8 rounded-full border-2 border-transparent hover:border-[#FF3B30] transition-colors object-cover"
                    data-testid="user-avatar"
                  />
                </Link>
                <button
                  onClick={handleLogout}
                  className="text-sm font-medium hover:text-[#FF3B30] transition-colors"
                  data-testid="logout-button"
                >
                  로그아웃
                </button>
              </div>
            ) : (
              <Link
                to="/login"
                className="px-4 py-2 bg-[#FF3B30] hover:bg-[#D32F2F] text-white rounded-md text-sm font-medium transition-colors"
                data-testid="login-button"
              >
                로그인
              </Link>
            )}
          </div>

          {/* Mobile: search + theme toggle + hamburger */}
          <div className="flex md:hidden items-center gap-2">
            <button
              onClick={() => setSearchOpen((v) => !v)}
              className="p-2 rounded-md hover:bg-black/5 dark:hover:bg-white/10 transition-colors"
              aria-label="검색"
              data-testid="nav-search-toggle-mobile"
            >
              <Search size={18} />
            </button>
            <button
              onClick={toggleTheme}
              className="p-2 rounded-md hover:bg-black/5 dark:hover:bg-white/10 transition-colors"
              data-testid="theme-toggle-mobile"
              aria-label="Toggle theme"
            >
              {theme === 'light' ? <Moon size={18} /> : <Sun size={18} />}
            </button>
            <button
              onClick={() => setMobileMenuOpen((v) => !v)}
              className="p-2 rounded-md hover:bg-black/5 dark:hover:bg-white/10"
              data-testid="mobile-menu-toggle"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </div>

      {mobileMenuOpen && (
        <div
          className="md:hidden border-t border-black/5 dark:border-white/10 bg-white dark:bg-black"
          data-testid="mobile-menu"
        >
          <div className="px-4 py-4 space-y-3">
            {renderCoreLinks(true)}
            {user ? (
              <button
                onClick={() => { setMobileMenuOpen(false); handleLogout(); }}
                className="block w-full text-left text-sm font-medium hover:text-[#FF3B30]"
                data-testid="logout-button-mobile"
              >
                로그아웃
              </button>
            ) : (
              <Link
                to="/login"
                className="block text-sm font-medium hover:text-[#FF3B30]"
                onClick={() => setMobileMenuOpen(false)}
                data-testid="login-button-mobile"
              >
                로그인
              </Link>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navigation;
